/**
 * Chat Store
 * Main store for chat functionality - message sending and UI state
 * Uses conversationStore and researchStatusStore for specific operations
 *
 * Performance: Use atomic selectors for granular subscriptions
 * to prevent unnecessary re-renders in consuming components.
 */
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type { Message } from '../../lib/types'
import { conversationsApi, chatApi, feedbackApi, extractApiError } from '../../lib/api'
import { logger } from '@/lib/logger'
import { useConversationStore } from './conversationStore'
import { useResearchStatusStore } from './researchStatusStore'
import { useSettingsStore } from '../settingsStore'
import { useTierStore } from '../tierStore'
import { adaptChatResponse } from './adaptChatResponse'
import type { OrchestrationMode } from './types'
import { trackEvent } from '../../lib/analytics'

interface ChatState {
  // Model selection state
  mode: OrchestrationMode
  selectedModels: string[]
  researchMode: boolean

  // Message sending state
  isSending: boolean
  error: string | null
  unavailableModelsWarning: string[] | null

  // UI state
  expandedTransparencyPanels: Record<string, boolean>

  // Actions
  sendMessage: (content: string) => Promise<void>
  setMode: (mode: OrchestrationMode) => void
  setSelectedModels: (models: string[]) => void
  setResearchMode: (enabled: boolean) => void
  initializeModeFromSettings: () => void
  selectResponseForMessage: (messageId: string, modelId: string) => void
  toggleTransparencyPanel: (messageId: string) => void
  clearError: () => void
  reset: () => void
}

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial state
  mode: 'auto',
  selectedModels: [],
  researchMode: false,
  isSending: false,
  error: null,
  unavailableModelsWarning: null,
  expandedTransparencyPanels: {},

  /**
   * Send a message in the current conversation
   */
  sendMessage: async (content: string) => {
    const { mode, selectedModels, researchMode } = get()
    const conversationStore = useConversationStore.getState()
    const researchStatusStore = useResearchStatusStore.getState()
    let currentConversation = conversationStore.currentConversation

    if (!currentConversation) {
      set({ error: 'No conversation selected' })
      return
    }

    set({ isSending: true, error: null })

    // Define tempUserId at function scope
    let tempUserId = ''

    // Determines if we need to subscribe to SSE for status updates
    const mayBeComplexQuery = mode === 'auto' || (mode === 'manual' && selectedModels.length >= 2)
    const shouldTrackStatus = researchMode || mayBeComplexQuery

    try {
      // If this is a temporary conversation, create a real one first
      if (currentConversation.id.startsWith('temp-')) {
        const { generateTitle } = await import('../../lib/generateTitle')
        const title = generateTitle(content)

        const response = await conversationsApi.create(title)

        if (!response.success || !response.data) {
          throw new Error('Failed to create conversation')
        }

        const newConversation = response.data

        // Update conversation store with the new real conversation
        conversationStore.addConversation(newConversation)
        conversationStore.setCurrentConversation(newConversation)

        // Update local reference
        currentConversation = newConversation
      }

      // Implicit feedback detection (E1.5.1 Option E — Tier 1)
      // Analyze the previous interaction for implicit signals before sending new message
      const existingMessages = currentConversation.messages
      if (existingMessages && existingMessages.length >= 2) {
        const lastMsg = existingMessages[existingMessages.length - 1]
        const prevUserMsg = existingMessages[existingMessages.length - 2]

        // Only analyze if last message is assistant and has trace data
        if (lastMsg.role === 'assistant' && lastMsg.metadata?.trace?.id) {
          const traceId = lastMsg.metadata.trace.id
          const feedbackType = lastMsg.metadata?.selectedResponseModel === 'synthesized' ? 'synthesis' as const : 'selection' as const
          const displayedModel = lastMsg.metadata?.selectedResponseModel || lastMsg.metadata?.bestModel
          const newContent = content.toLowerCase().trim()

          // Signal 1: Positive keywords → implicit thumbs up
          const positivePatterns = /^(thanks|thank you|perfect|great|awesome|exactly|that'?s? (right|correct|helpful|what i needed))/i
          if (positivePatterns.test(newContent)) {
            feedbackApi.submitImplicit({
              traceId,
              messageId: lastMsg.id,
              score: 'implicit_positive',
              feedbackType,
              displayedModel,
              signalSource: 'positive_keyword'
            })
          }

          // Signal 2: Re-phrase detection → implicit thumbs down
          // If >60% of words match previous query, likely a re-phrase
          if (prevUserMsg.role === 'user') {
            const prevWords = new Set(prevUserMsg.content.toLowerCase().split(/\s+/))
            const newWords = newContent.split(/\s+/)
            const overlap = newWords.filter(w => prevWords.has(w)).length / Math.max(newWords.length, 1)
            if (overlap > 0.6 && newWords.length > 3) {
              feedbackApi.submitImplicit({
                traceId,
                messageId: lastMsg.id,
                score: 'implicit_negative',
                feedbackType,
                displayedModel,
                signalSource: 'rephrase'
              })
            }
          }
        }
      }

      // Optimistically add user message to UI
      tempUserId = `temp-user-${Date.now()}`
      const userMessage: Message = {
        id: tempUserId,
        conversationId: currentConversation.id,
        role: 'user',
        content,
        createdAt: new Date().toISOString()
      }

      conversationStore.updateConversationMessages(conv => ({
        ...conv,
        messages: [...(conv.messages || []), userMessage]
      }))

      // Generate requestId if tracking status
      const requestId = shouldTrackStatus ? `${currentConversation.id}-${Date.now()}` : undefined

      // Subscribe to SSE for status updates BEFORE making the API call
      if (shouldTrackStatus && requestId) {
        researchStatusStore.subscribeToResearchStatus(requestId, researchMode)
      }

      const response = await chatApi.sendMessage({
        conversationId: currentConversation.id,
        message: content,
        mode,
        selectedModels: mode === 'manual' ? selectedModels : undefined,
        researchMode,
        requestId
      })

      if (response.success && response.data) {
        const { messageId } = response.data

        // Fail HERE if the server sent no id, rather than three lines down.
        //
        // Frontend and control-plane are separate Railway services, so a
        // version skew is reachable: a frontend deployed ahead of the
        // control-plane that adds `messageId` (or a control-plane rolled back
        // behind it) serves `undefined` here, and the type says `string`.
        //
        // Without this the message is stored with `id: undefined` and the
        // damage lands on the NEXT send, where the history filter below runs
        // `m.id.startsWith('temp-assistant-')` over it and throws
        // `Cannot read properties of undefined`. That discards a response the
        // server had already produced and billed, marks the user's message
        // failed, and prints a raw TypeError into the chat.
        //
        // No fabricated fallback, deliberately: minting an id is the defect
        // this whole change removes. An honest outage message during a
        // deploy window is better than an answer that silently vanishes on
        // the following turn.
        if (!messageId) {
          throw new Error('Server did not return a message id')
        }

        // Wire → domain. Every field mapping, the epistemic-label/fact-check
        // join and the explicit allowlist live in `adaptChatResponse`, which is
        // pure and unit-tested (AUD-tail-arch A4). The store keeps only what is
        // genuinely store work: the guard above, and the writes below.
        //
        // The clock is passed in rather than read there, so the adapter has no
        // hidden input and its tests need no fake timers.
        const { message: assistantMessage, unavailableModels } = adaptChatResponse(response.data, {
          conversationId: currentConversation.id,
          createdAt: new Date().toISOString()
        })

        // Update messages: replace temp user message with real one, add assistant message
        conversationStore.updateConversationMessages(conv => ({
          ...conv,
          messages: [
            ...(conv.messages || [])
              .filter(m => m.id !== tempUserId)
              .filter(m => !m.id.startsWith('temp-assistant-')),
            userMessage,
            assistantMessage
          ]
        }))

        set({
          isSending: false,
          unavailableModelsWarning: unavailableModels
        })

        // Clear research status polling on success
        if (shouldTrackStatus) {
          researchStatusStore.clearResearchStatus()
        }

        // Refresh the free-tier trial counters. A chat is the ONLY thing that
        // spends trial credits, and `loadSimpleTrialInfo` was called from exactly
        // one place — an AppLayout effect keyed on the user — so the banner kept
        // showing the balance from page load until a reload. A user who spent
        // their daily allowance in one sitting saw "40 of 75 available today" and
        // then got a 429, which is precisely the "looks like the product broke"
        // failure the daily figure was added to prevent.
        //
        // Fire-and-forget and free-tier only: it must never delay or fail a
        // delivered message, and no other tier has a trial to refresh.
        //
        // Deliberately not awaited and deliberately unhandled here.
        // `loadSimpleTrialInfo` wraps its whole body in try/catch and logs
        // (`tierStore.ts`: `logger.warn('Failed to load simple trial info')`),
        // so it never rejects — the `.catch()` that used to sit here was
        // unreachable code presented as an error-handling decision. `void` is
        // not the alternative either: `sonarjs/void-use` flags it and the
        // eslint ratchet is a hard gate. The failure IS logged, one layer down.
        if (useTierStore.getState().currentTier === 'free') {
          useTierStore.getState().loadSimpleTrialInfo()
        }
      } else {
        throw new Error(extractApiError(response, 'Failed to send message'))
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message'

      // Keep the user's message but mark as failed
      conversationStore.updateConversationMessages(conv => ({
        ...conv,
        messages: (conv.messages || []).map(m =>
          m.id === tempUserId ? { ...m, failed: true } : m
        )
      }))

      set({
        error: errorMessage,
        isSending: false
      })

      // Clear status on error
      if (shouldTrackStatus) {
        setTimeout(() => {
          researchStatusStore.clearResearchStatus()
        }, 1000)
      }
    }
  },

  /**
   * Set orchestration mode (auto/manual)
   */
  setMode: (mode: OrchestrationMode) => {
    const previousMode = get().mode
    if (previousMode === mode) return
    set({ mode })
    const conversationId = useConversationStore.getState().currentConversation?.id
    trackEvent('mode_switched', {
      previous_mode: previousMode,
      new_mode: mode,
      conversation_id: conversationId ?? '',
    })
  },

  /**
   * Set selected models for manual mode
   */
  setSelectedModels: (models: string[]) => {
    set({ selectedModels: models })
  },

  /**
   * Set research mode (enables deep research with citations)
   */
  setResearchMode: (enabled: boolean) => {
    set({ researchMode: enabled })
  },

  /**
   * Initialize mode from user's default settings
   * Called when creating a new conversation to respect user's preference
   */
  initializeModeFromSettings: () => {
    const settingsStore = useSettingsStore.getState()
    const defaultMode = settingsStore.settings?.mode || 'auto'
    set({ mode: defaultMode })
  },

  /**
   * Select a different model response for a message
   */
  selectResponseForMessage: (messageId: string, modelId: string) => {
    const conversationStore = useConversationStore.getState()
    let resolvedModelId = modelId

    conversationStore.updateConversationMessages(conv => ({
      ...conv,
      messages: conv.messages?.map(msg => {
        if (msg.id !== messageId) return msg

        // If modelId is 'synthesized', show the merged response
        if (modelId === 'synthesized' && msg.metadata?.synthesized) {
          return {
            ...msg,
            content: msg.metadata.synthesized.content,
            metadata: {
              ...msg.metadata,
              selectedResponseModel: 'synthesized'
            }
          }
        }

        // If modelId is empty, show best model's individual response (default)
        if (modelId === '' && msg.metadata?.bestModel && msg.metadata?.responses) {
          const bestResponse = msg.metadata.responses.find(r => r.model === msg.metadata?.bestModel)
          if (bestResponse) {
            resolvedModelId = msg.metadata.bestModel
            return {
              ...msg,
              content: bestResponse.content,
              metadata: {
                ...msg.metadata,
                selectedResponseModel: msg.metadata.bestModel
              }
            }
          }
        }

        // Otherwise, find and select the specific model's response
        if (msg.metadata?.responses) {
          const selectedResponse = msg.metadata.responses.find(r => r.model === modelId)
          if (selectedResponse) {
            return {
              ...msg,
              content: selectedResponse.content,
              metadata: {
                ...msg.metadata,
                selectedResponseModel: modelId
              }
            }
          }
        }

        return msg
      })
    }))

    // Persist selectedResponseModel to DB (fire-and-forget)
    const convId = conversationStore.currentConversation?.id
    if (convId && !convId.startsWith('temp-') && !messageId.startsWith('temp-')) {
      conversationsApi.updateMessageMetadata(convId, messageId, {
        selectedResponseModel: resolvedModelId
      }).catch(error => logger.warn('Failed to persist selectedResponseModel', error))
    }
  },

  /**
   * Toggle transparency panel expansion state for a message
   */
  toggleTransparencyPanel: (messageId: string) => {
    set(state => {
      const { [messageId]: current, ...rest } = state.expandedTransparencyPanels
      return {
        expandedTransparencyPanels: current
          ? rest  // remove the key (collapse)
          : { ...state.expandedTransparencyPanels, [messageId]: true }  // add the key (expand)
      }
    })
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null, unavailableModelsWarning: null })
  },

  /**
   * Reset entire chat store state (used on logout)
   */
  reset: () => {
    // Also reset related stores
    useConversationStore.getState().reset()
    useResearchStatusStore.getState().reset()

    set({
      mode: 'auto',
      selectedModels: [],
      researchMode: false,
      isSending: false,
      error: null,
      unavailableModelsWarning: null,
      expandedTransparencyPanels: {}
    })
  }
}))

// ============================================================================
// ATOMIC SELECTORS
// Use these for granular subscriptions to prevent unnecessary re-renders
// ============================================================================

/**
 * Select only the orchestration mode
 * Use when component only needs to know the current mode
 */
export const useChatMode = () => useChatStore(state => state.mode)

/**
 * Select only selected models for manual mode
 */
export const useSelectedModels = () => useChatStore(state => state.selectedModels)

/**
 * Select only research mode status
 */
export const useResearchMode = () => useChatStore(state => state.researchMode)

/**
 * Select only sending state
 * Use in submit buttons, loading indicators
 */
export const useIsSending = () => useChatStore(state => state.isSending)

/**
 * Select only error state
 * Use in error displays, toast notifications
 */
export const useChatError = () => useChatStore(state => state.error)

/**
 * Select only warning state
 */
export const useUnavailableModelsWarning = () => useChatStore(state => state.unavailableModelsWarning)

/**
 * Select model selection state (mode + selected models)
 * Use in model selector components
 */
export const useModelSelection = () => useChatStore(
  useShallow(state => ({
    mode: state.mode,
    selectedModels: state.selectedModels,
    setMode: state.setMode,
    setSelectedModels: state.setSelectedModels
  }))
)

/**
 * Select all mode-related state (for input area)
 * Use in chat input components that need mode, models, and research mode
 */
export const useChatInputState = () => useChatStore(
  useShallow(state => ({
    mode: state.mode,
    selectedModels: state.selectedModels,
    researchMode: state.researchMode,
    isSending: state.isSending,
    setMode: state.setMode,
    setSelectedModels: state.setSelectedModels,
    setResearchMode: state.setResearchMode,
    sendMessage: state.sendMessage
  }))
)

/**
 * Select actions only (no state - stable reference)
 * Use when you only need to dispatch actions
 */
export const useChatActions = () => useChatStore(
  useShallow(state => ({
    sendMessage: state.sendMessage,
    setMode: state.setMode,
    setSelectedModels: state.setSelectedModels,
    setResearchMode: state.setResearchMode,
    initializeModeFromSettings: state.initializeModeFromSettings,
    selectResponseForMessage: state.selectResponseForMessage,
    toggleTransparencyPanel: state.toggleTransparencyPanel,
    clearError: state.clearError,
    reset: state.reset
  }))
)

/**
 * Check if a transparency panel is expanded
 */
export const useIsTransparencyPanelExpanded = (messageId: string) =>
  useChatStore(state => !!state.expandedTransparencyPanels[messageId])
